/**
 * Die Feldtafel des DATEV-Formats — Chip `DX-satzbau`, Task 3 des Plans
 * `docs/superpowers/plans/2026-09-08-datev-export-umsetzung.md`.
 *
 * ===========================================================================
 * DIESE DATEI IST DIE EINE STELLE, AN DER DAS FORMAT STEHT
 * ===========================================================================
 *
 * **Quelle: `docs/datev/extf-felder-kopfzeile.md` und
 * `docs/datev/extf-felder-buchungsstapel.md`** — die eigenen Feldtabellen aus
 * Task 1 (`DX-format`, gelandet am 2026-09-08). Jede Zahl, jede Reihenfolge
 * und jede Laenge dieser Datei ist gegen jene Tabellen gehalten; wer sie
 * aendert, aendert zuerst dort und dann hier, und keine Regel
 * (`zerlegung.ts`, `satz.ts`, `kopfzeile.ts`,
 * `apps/api/src/datev/satzbau.ts`) fasst eine Feldnummer selbst an.
 *
 * **Der Plan-Nachtrag zu Task 3 (2026-09-08)** haelt fest, dass diese Datei
 * zuerst gegen die Feldliste der Spec (Abschnitt 3.2, damals durchgehend
 * `[UNGEKLAERT]`) gebaut und mit dem Landen von `DX-format` gegen die
 * eigenen Feldtabellen nachgezogen wurde. Was danach noch offen ist, traegt
 * hier weiterhin die Marke `[UNGEKLAERT]` und ist damit benannte Annahme und
 * keine Zusicherung.
 */
// ===========================================================================
// 1. DIE KOPFZEILE — 31 FELDER
// ===========================================================================
/** Feld 1: `EXTF` — die Datei stammt von einem fremden Erzeuger, nicht von DATEV. */
export const DATEV_KENNZEICHEN = 'EXTF';
/**
 * Feld 2: die Versionsnummer des **Kopfzeilenaufbaus**, zur Zeit ausschliesslich `700`.
 *
 * Nicht zu verwechseln mit {@link FORMATVERSION_BUCHUNGSSTAPEL} (Feld 5) —
 * genau diese Verwechslung nennt `docs/datev/extf-felder-kopfzeile.md` als
 * den Fehler, den mehrere Zweitquellen machen.
 */
export const DATEV_VERSIONSNUMMER = '700';
/** Feld 5: die Fassung des **Buchungssatzaufbaus**, seit Februar 2024 unveraendert (`D-3`). */
export const FORMATVERSION_BUCHUNGSSTAPEL = '13';
/** Feld 5 fuer die Debitoren-/Kreditorenstammdaten (Task 7). */
export const FORMATVERSION_DEBITOREN_KREDITOREN = '5';
/** Feld 5 fuer die Sachkontenbeschriftungen (Task 7). */
export const FORMATVERSION_KONTENBESCHRIFTUNGEN = '3';
/**
 * Feld 14: Sachkontenlaenge, vierstellig.
 *
 * Sie ist zugleich der stille Beweis, dass der Debitorenkreis 10000-69999
 * (Migration `0013`, `D-34`) zur DATEV-Konvention passt: Ein Personenkonto
 * ist genau **Sachkontenlaenge + 1** Stellen lang, hier also fuenfstellig.
 */
export const SACHKONTENLAENGE = '4';
/** Feld 22: alles ist `EUR`; eine Fremdwaehrung baut diese Aera ausdruecklich nicht. */
export const WAEHRUNGSKENNZEICHEN = 'EUR';
/** Feld 19: `1` Finanzbuchfuehrung (`2` waere der Jahresabschluss). */
export const BUCHUNGSTYP_FINANZBUCHFUEHRUNG = '1';
/** Feld 20: `0` — rechnungslegungszweckunabhaengig. */
export const RECHNUNGSLEGUNGSZWECK_UNABHAENGIG = '0';
/** Feld 27: SKR03, wie ihn `0010_skr03.sql` seedet. */
export const SACHKONTENRAHMEN_SKR03 = '03';
/** Feld 21 der Kopfzeile und Feld 114 des Satzes: `1`, weil nur geschlossene Perioden gehen (`D-9`). */
export const FESTSCHREIBUNG_JA = '1';
/** Feld 118: `0` — keine Generalumkehr im ersten Bau (`D-8`). */
export const GENERALUMKEHR_NEIN = '0';
/**
 * Das Gegenkonto des Saldenvortrags je Personenkonto — Chip `DX-vortrag`,
 * Task 8 (`D-15`).
 *
 * **Weder eigene Datenkategorie noch eigener Buchungstyp** (Spec Abschnitt 7,
 * Nachtrag vom 2026-09-08 zu `D-15`): Die Quelle fuehrt acht Datenkategorien
 * und keine davon ist ein Vortrag; der Vortrag geht als gewoehnlicher
 * `buchungsstapel` hinaus (`DATENKATEGORIE_TAFEL.buchungsstapel`), und was
 * ihn zum Vortrag macht, ist ausschliesslich das Gegenkonto.
 *
 * **`9008` „Saldenvortraege, Debitoren" und ausdruecklich nicht `9000`.** Der
 * SKR03 2026 des Auftraggebers fuehrt fuer Sachkonten `9000` (das Konto, das
 * `KONTO_SALDENVORTRAG` in `apps/api/src/guthaben/konten.ts` **fuer unser
 * eigenes Journal** traegt), fuer Debitoren `9008`, fuer Kreditoren `9009`.
 * Unsere 35 Eroeffnungsbuchungen aus `pnpm altimport:altforderungen` stehen
 * bei UNS gegen `9000` (Sachkonten, Migration `0159`, `E-844`) — das bleibt
 * unveraendert, denn dort traegt die Buchung keinen Personenbezug. **Dieser
 * Export UEBERSETZT dieselbe Herkunft an der Grenze**, genau wie er ein
 * Sachkonto-Forderungskonto (`1400`) durch ein Personenkonto ersetzt (`D-6`):
 * Fuer eine Lieferung **je Personenkonto** ist `9008` das dafuer vorgesehene
 * Konto, und das Nachweisblatt (Task 11) nennt, dass die Gegenbuchung im
 * eigenen Hauptbuch auf `9000` steht.
 *
 * **Benannte Annahme, unter der gebaut wird** (Plan-Nachtrag zu Task 8,
 * 2026-09-08): Die Spec markiert die Kontonummer selbst als Auskunft der
 * Kanzlei und nicht als Entscheidung dieses Hauses — bis eine Rueckmeldung
 * widerspricht, gilt `9008`. Wer das aendert, aendert zuerst den Plan.
 */
export const SALDENVORTRAG_GEGENKONTO_DEBITOREN = '9008';
/**
 * Die Datenkategorien, die diese Aera liefert.
 *
 * `saldenvortrag` steht bewusst nicht darin: `D-15` ist beantwortet (siehe
 * {@link SALDENVORTRAG_GEGENKONTO_DEBITOREN}) — der Vortrag ist keine eigene
 * Datenkategorie, sondern ein gewoehnlicher `buchungsstapel` mit `9008` als
 * Gegenkonto. `saldenvortrag` bleibt trotzdem der Name der REGISTERZEILE
 * (`datev_export.kategorie`, Migration `0187`): Das Register fuehrt, WELCHE
 * der fuenf Lieferungen es war, unabhaengig davon, welche Datenkategorie die
 * Datei selbst traegt.
 */
export const DATENKATEGORIEN = [
    'buchungsstapel',
    'debitoren_kreditoren',
    'kontenbeschriftungen',
];
/**
 * Felder 3, 4 und 5 je Datenkategorie.
 *
 * **Der Formatname der Kategorie 20 ist `Sachkontenbeschriftungen`** und
 * nicht `Kontenbeschriftungen`: Die Uebersichtstabelle der Quelle nennt das
 * eine, die Formvorgabe fuer Feld 4 das andere, und geprueft wird die Datei
 * an der Formvorgabe. Der Widerspruch ist in
 * `docs/datev/extf-felder-kopfzeile.md` gemessen und benannt.
 */
export const DATENKATEGORIE_TAFEL = {
    buchungsstapel: {
        code: '21',
        formatname: 'Buchungsstapel',
        formatversion: FORMATVERSION_BUCHUNGSSTAPEL,
    },
    debitoren_kreditoren: {
        code: '16',
        formatname: 'Debitoren/Kreditoren',
        formatversion: FORMATVERSION_DEBITOREN_KREDITOREN,
    },
    kontenbeschriftungen: {
        code: '20',
        formatname: 'Sachkontenbeschriftungen',
        formatversion: FORMATVERSION_KONTENBESCHRIFTUNGEN,
    },
};
/** Die 31 Felder der Kopfzeile, in der Folge, in der sie geschrieben werden. */
export const KOPFZEILE_FELDER = [
    'Kennzeichen',
    'Versionsnummer',
    'Formatkategorie',
    'Formatname',
    'Formatversion',
    'Erzeugt am',
    'Importiert',
    'Herkunft',
    'Exportiert von',
    'Importiert von',
    'Beraternummer',
    'Mandantennummer',
    'WJ-Beginn',
    'Sachkontenlaenge',
    'Datum von',
    'Datum bis',
    'Bezeichnung',
    'Diktatkuerzel',
    'Buchungstyp',
    'Rechnungslegungszweck',
    'Festschreibung',
    'WKZ',
    'Reserviert 23',
    'Derivatskennzeichen',
    'Reserviert 25',
    'Reserviert 26',
    'Sachkontenrahmen',
    'ID der Branchenloesung',
    'Reserviert 29',
    'Reserviert 30',
    'Anwendungsinformation',
];
/**
 * Die Stelle jedes belegten Kopfzeilenfeldes, **einsbasiert wie die amtliche
 * Zaehlung**.
 *
 * Einsbasiert und nicht nullbasiert, weil jede Fundstelle — die eigene
 * Feldtabelle, die Quelle, jede Ruecksprache mit der Kanzlei — so zaehlt. Ein
 * Register, das um eins verschoben zaehlt, ist genau die Sorte Fehler, die
 * beim Lesen nicht auffaellt.
 */
export const KOPFZEILE_STELLE = {
    kennzeichen: 1,
    versionsnummer: 2,
    formatkategorie: 3,
    formatname: 4,
    formatversion: 5,
    erzeugtAm: 6,
    herkunft: 8,
    exportiertVon: 9,
    beraternummer: 11,
    mandantennummer: 12,
    wjBeginn: 13,
    sachkontenlaenge: 14,
    datumVon: 15,
    datumBis: 16,
    bezeichnung: 17,
    diktatkuerzel: 18,
    buchungstyp: 19,
    rechnungslegungszweck: 20,
    festschreibung: 21,
    wkz: 22,
    derivatskennzeichen: 24,
    sachkontenrahmen: 27,
    reserviert30: 30,
    anwendungsinformation: 31,
};
/** Feld 17: die Bezeichnung des Stapels, hoechstens 30 Zeichen. */
export const BEZEICHNUNG_MAX_ZEICHEN = 30;
/** Feld 31: die Anwendungsinformation, hoechstens 16 Zeichen. */
export const ANWENDUNGSINFORMATION_MAX_ZEICHEN = 16;
/**
 * Feld 8: das Herkunftskennzeichen, zwei Grossbuchstaben.
 *
 * Wird beim Import ohnehin durch `SV` ersetzt; wir schreiben trotzdem eine
 * benannte Herkunft, damit eine Datei auf dem Weg dorthin ihren Erzeuger
 * nennt.
 */
export const HERKUNFT_KENNZEICHEN = 'FA';
// ===========================================================================
// 2. DER BUCHUNGSSATZ — 125 FELDER, FORMATVERSION 13
// ===========================================================================
/** Feld 14: der Buchungstext traegt hoechstens 60 Zeichen. */
export const BUCHUNGSTEXT_MAX_ZEICHEN = 60;
/**
 * Feld 11: Belegfeld 1 traegt hoechstens **36** Zeichen.
 *
 * Die aeltere Feldbeschreibung nennt zwoelf; das ist eine echte
 * Formataenderung und keine Lesart (`docs/datev/extf-felder-buchungsstapel.md`).
 */
export const BELEGFELD1_MAX_ZEICHEN = 36;
/**
 * Der Zeichenvorrat von Belegfeld 1: Buchstaben, Ziffern, Unterstrich und
 * `$ & % * + - /`.
 *
 * Ausdruecklich **unzulaessig** sind Leerzeichen, Umlaute, Punkt, Komma,
 * Semikolon und Doppelpunkt. Unsere Rechnungsnummern halten das ein
 * (`number_range.number_prefix` ist auf `^[A-Z]{0,3}-?$` begrenzt, Migration
 * `0069`); ob die **Aufladungskennung** es tut, ist an ihrer Quelle zu messen
 * und gehoert nach Vorpruefung 10 (Task 6). Die Konstante steht hier, damit
 * Task 6 sie liest und nicht ein zweites Mal aufschreibt.
 */
export const BELEGFELD1_ZEICHENVORRAT = /^[A-Za-z0-9_$&%*+\-/]*$/;
/** Feld 12: Belegfeld 2 traegt hoechstens zwoelf Zeichen — bleibt leer (`D-7`). */
export const BELEGFELD2_MAX_ZEICHEN = 12;
/**
 * Die Stelle jedes belegten Satzfeldes, **einsbasiert wie die amtliche
 * Zaehlung**. Alle uebrigen der 125 Stellen bleiben leer.
 */
export const SATZ_STELLE = {
    umsatz: 1,
    sollHaben: 2,
    wkzUmsatz: 3,
    konto: 7,
    gegenkonto: 8,
    buSchluessel: 9,
    belegdatum: 10,
    belegfeld1: 11,
    belegfeld2: 12,
    buchungstext: 14,
    beleglink: 20,
    kost1: 37,
    kost2: 38,
    euLandUstIdBestimmung: 40,
    euSteuersatzBestimmung: 41,
    abweichendeVersteuerungsart: 42,
    festschreibung: 114,
    leistungsdatum: 115,
    datumSteuerperiode: 116,
    generalumkehr: 118,
};
/**
 * Die Wiederholungsbloecke als Feldnamen.
 *
 * Die eigene Feldtabelle fuehrt sie als **eine** Zeile (`21-36`, `48-87`),
 * weil sie sonst zwei Bildschirmseiten fuellten. Die Ueberschriftenzeile
 * braucht sie einzeln — deshalb werden sie hier erzeugt und nicht
 * abgeschrieben: 56 von Hand getippte Feldnamen sind 56 Gelegenheiten fuer
 * einen Zahlendreher.
 */
function wiederholung(name, anzahl) {
    const felder = [];
    for (let nummer = 1; nummer <= anzahl; nummer += 1) {
        felder.push(`${name} - Art ${nummer}`, `${name} - Inhalt ${nummer}`);
    }
    return felder;
}
/**
 * Die 125 Felder eines Buchungssatzes, in der Folge, in der sie geschrieben
 * werden. **Zugleich die Ueberschriftenzeile** — `E-1002` haelt jede
 * Datenzeile gegen ihre Laenge.
 */
export const BUCHUNGSSATZ_FELDER = [
    'Umsatz (ohne Soll/Haben-Kz)',
    'Soll/Haben-Kennzeichen',
    'WKZ Umsatz',
    'Kurs',
    'Basisumsatz',
    'WKZ Basisumsatz',
    'Konto',
    'Gegenkonto (ohne BU-Schluessel)',
    'BU-Schluessel',
    'Belegdatum',
    'Belegfeld 1',
    'Belegfeld 2',
    'Skonto',
    'Buchungstext',
    'Postensperre',
    'Diverse Adressnummer',
    'Geschaeftspartnerbank',
    'Sachverhalt',
    'Zinssperre',
    'Beleglink',
    ...wiederholung('Beleginfo', 8),
    'KOST1 - Kostenstelle',
    'KOST2 - Kostenstelle',
    'KOST-Menge',
    'EU-Mitgliedstaat u. UStID (Bestimmung)',
    'EU-Steuersatz (Bestimmung)',
    'Abw. Versteuerungsart',
    'Sachverhalt L+L',
    'Funktionsergaenzung L+L',
    'BU 49 Hauptfunktionstyp',
    'BU 49 Hauptfunktionsnummer',
    'BU 49 Funktionsergaenzung',
    ...wiederholung('Zusatzinformation', 20),
    'Stueck',
    'Gewicht',
    'Zahlweise',
    'Forderungsart',
    'Veranlagungsjahr',
    'Zugeordnete Faelligkeit',
    'Skontotyp',
    'Auftragsnummer',
    'Buchungstyp',
    'USt-Schluessel (Anzahlungen)',
    'EU-Mitgliedstaat (Anzahlungen)',
    'Sachverhalt L+L (Anzahlungen)',
    'EU-Steuersatz (Anzahlungen)',
    'Erloeskonto (Anzahlungen)',
    'Herkunft-Kz',
    'Buchungs GUID',
    'KOST-Datum',
    'SEPA-Mandatsreferenz',
    'Skontosperre',
    'Gesellschaftername',
    'Beteiligtennummer',
    'Identifikationsnummer',
    'Zeichnernummer',
    'Postensperre bis',
    'Bezeichnung SoBil-Sachverhalt',
    'Kennzeichen SoBil-Buchung',
    'Festschreibung',
    'Leistungsdatum',
    'Datum Zuord. Steuerperiode',
    'Faelligkeit',
    'Generalumkehr (GU)',
    'Steuersatz',
    'Land',
    'Abrechnungsreferenz',
    'BVV-Position',
    'EU-Mitgliedstaat u. UStID (Ursprung)',
    'EU-Steuersatz (Ursprung)',
    'Abw. Skontokonto',
];
//# sourceMappingURL=felder.js.map